import { ContentSlug } from "@robe/core-api/content/getContentSlugsByIds";
import { RichtextContentsLinksFetchedData, Localized, RichtextValue, ContentValues, FileValue } from "@robe/core-types";
import { XcoreCore } from "XcoreCore";
import {
  isRichtextValue,
  isLocalizedRichtextValue,
  resolveLocalizedRichtextValueLinks,
  isUnfetchedFieldValue,
  isLocalizedUnfetchedFieldValue,
} from "./resolvers";

/**
 * There are these possible structures for richtext value:
 * - Simple richtext value
 * - Localized simple richtext value
 * - Single field type list richtext value
 * - Localized single field type list richtext value
 * - Object list richtext value
 * - Localized object list richtext value
 */
type PseudoValue = {
  [id: string]:
    | RichtextValue
    | Localized<RichtextValue>
    | {
        [id: string]: RichtextValue | Localized<RichtextValue>;
      }[];
};

/** Create array of unique content ids from links and fetch contents slugs for these content ids */
export const resolveContentsSlugsByIds = async (core: XcoreCore, linksContentIds: number[]) => {
  if (linksContentIds.length === 0) {
    return [];
  }

  const linksContentIdsUnique = Array.from(new Set(linksContentIds));

  const contentsSlugsFetched = await core.content.slugsByIds(linksContentIdsUnique);
  // When content is removed it's content links in other contents richtexts aren't removed
  if (!contentsSlugsFetched || contentsSlugsFetched.length === 0) {
    return null;
  }

  return contentsSlugsFetched;
};

export const mapFetchedContentsSlugs = (contentsSlugsFetched: ContentSlug[]): RichtextContentsLinksFetchedData => {
  if (contentsSlugsFetched.length === 0) {
    return [];
  }

  return Object.fromEntries(
    contentsSlugsFetched.map(({ contentId, localizedSlugs, contentType }) => [contentId, { slug: localizedSlugs, contentType }])
  );
};

/**
 * Recursively go through all content values and if any of them are richtext values
 * return content ids from links if there are any.
 * */
export const collectContentIdsFromRichtextContentLinks = (values: ContentValues, locales: string[]): number[] =>
  Object.values(values)
    .map((value) => {
      if (!value) {
        return [];
      }

      if (isRichtextValue(value)) {
        return value.links;
      }

      // Localized value or localized list value
      if (isLocalizedRichtextValue(value, locales)) {
        return resolveLocalizedRichtextValueLinks(value, locales);
      }

      // List value
      if (Array.isArray(value)) {
        return value
          .map((listValue) => {
            // Single field list value
            if (isRichtextValue(listValue)) {
              return listValue.links;
            }

            // List object value
            if (typeof listValue === "object") {
              return collectContentIdsFromRichtextContentLinks(listValue, locales);
            }

            return [];
          })
          .flat();
      }

      return [];
    })
    .flat();

interface AssignUnfetchedValuesToContentValuesProps {
  contentsSlugs: RichtextContentsLinksFetchedData;
  locales: string[];
  values: ContentValues;
  imageInfos: FileValue[];
}

/**
 * Assign contents slugs to richtext values and file values to unfetched file fields
 */
export const assignUnfetchedValuesToContentValues = <T extends PseudoValue | RichtextValue | PseudoValue[]>({
  contentsSlugs,
  locales,
  values,
  imageInfos,
}: AssignUnfetchedValuesToContentValuesProps): T =>
    Object.fromEntries(
      Object.entries(values).map(([field, value]) => {
        if (!value) {
          return [field, value];
        }

        // Unfetched values are considered to be file values and are resolved only if found in image infos
        if (isUnfetchedFieldValue(value)) {
          const fileValueFound = imageInfos.find(({ id }) => id === value.id);

          if (fileValueFound) {
            return [field, fileValueFound];
          }
        }

        if (isRichtextValue(value)) {
          return [field, assignContentsSlugsToRichtextValue(value, contentsSlugs)];
        }

        // Localized value or localized list value
        if (isLocalizedUnfetchedFieldValue(value, locales)) {
          return [
            field,
            Object.fromEntries(
              Object.entries(value).map(([locale, val]) => [
                locale,
                Array.isArray(val)
                  ? val.map((listValue) => imageInfos.find(({ id }) => id === listValue.id) ?? listValue)
                  : (imageInfos.find(({ id }) => id === val.id) ?? val),
              ])
            ),
          ];
        }

        // Localized value or localized list value
        if (isLocalizedRichtextValue(value, locales)) {
          return [field, assignContentsSlugsToLocalizedRichtextValue(contentsSlugs, locales, value)];
        }

        // List value
        if (Array.isArray(value) && value.length > 0) {
          return [
            field,
            value.map((listValue) => {
            // Single field list value
              if (isRichtextValue(listValue)) {
                return assignContentsSlugsToRichtextValue(listValue, contentsSlugs);
              }

              // Unfetched values are considered to be file values and are resolved only if found in image infos
              if (isUnfetchedFieldValue(listValue)) {
                const fileValueFound = imageInfos.find((imageInfo) => imageInfo.id === listValue.id);

                if (fileValueFound) {
                  return fileValueFound;
                }
              }

              // List object value
              if (typeof listValue === "object") {
                return assignUnfetchedValuesToContentValues({
                  contentsSlugs,
                  locales,
                  values: listValue,
                  imageInfos,
                });
              }

              // Value is not a richtext value
              return listValue;
            }),
          ];
        }

        // Value is not a richtext value
        return [field, value];
      })
    );

const assignContentsSlugsToRichtextValue = (value: RichtextValue, contentsSlugsProvided: RichtextContentsLinksFetchedData) => {
  if (value.links.length === 0) {
    return value;
  }

  const contentsSlugs: RichtextContentsLinksFetchedData = {};

  value.links.forEach((contentId) => {
    // Assign fetched contents slugs data
    if (contentId in contentsSlugsProvided) {
      contentsSlugs[contentId] = contentsSlugsProvided[contentId];
    }
  });

  return {
    ...value,
    contentsSlugs,
  };
};

const assignContentsSlugsToLocalizedRichtextValue = (
  contentsSlugs: RichtextContentsLinksFetchedData,
  locales: string[],
  localizedValue: Record<string, RichtextValue | RichtextValue[]>
) =>
  Object.fromEntries(
    Object.entries(localizedValue).map(([locale, value]) =>
      locales.includes(locale)
        ? [
          locale,
          Array.isArray(value)
            ? value.map((listValue) => assignContentsSlugsToRichtextValue(listValue, contentsSlugs)) // Localized list value
            : assignContentsSlugsToRichtextValue(value, contentsSlugs),
        ]
        : [locale, value]
    )
  );
